import { Component, Input, OnInit, Optional, Self, ElementRef, OnDestroy, Output, EventEmitter, ViewChild } from '@angular/core';
import { MatLegacyFormFieldControl as MatFormFieldControl } from '@angular/material/legacy-form-field';
import { ControlValueAccessor, UntypedFormGroup, NgControl, UntypedFormBuilder, FormControl } from '@angular/forms';
import { Subject, Observable, of } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { FocusMonitor } from '@angular/cdk/a11y';
import { debounceTime, distinctUntilChanged, startWith, switchMap } from 'rxjs/operators';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';


@Component({
  selector: 'spolti-data-selector',
  templateUrl: './spolti-data-selector.component.html',
  styleUrls: ['./spolti-data-selector.component.css'],
  providers: [
    { provide: MatFormFieldControl, useExisting: SpoltiDataSelectorComponent },
  ],
  host: {
    '[class.floating]': 'shouldLabelFloat',
    '[id]': 'id',
    '[attr.aria-describedby]': 'describedBy',
  }
})
export class SpoltiDataSelectorComponent implements MatFormFieldControl<number>, ControlValueAccessor, OnInit, OnDestroy {

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;
  static nextId = 0;
  _value: number | null = null;
  public control = new FormControl('');

  @Output() idChanged = new EventEmitter<number | null>();

  @Input() getItemByIdFn!: (id: number) => Promise<any>;
  @Input() controllerName = 'x';
  @Input() methodNameForList = '';
  @Input() methodNameForItem = '';
  @Input() displayFn: (item: any) => string = item => item?.name ?? '';
  @Input() searchFn: (term: string) => Observable<any[]> = () => of([]);
  @Input() myCallback: Function;

  @Input()
  get placeholder(): string { return this._placeholder; }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  private _placeholder = '';

  @Input()
  get required(): boolean { return this._required; }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _disabled = false;

  @Input()
  get value(): number | null { return this._value; }
  set value(val: number | null) {
    val = val || 0;
    this._value = val;
    this.updateGui();
    this.stateChanges.next();
  }

  parts: UntypedFormGroup;
  stateChanges = new Subject<void>();
  focused = false;
  errorState = false;
  controlType = 'spolti-data-selector';
  id = `spolti-data-selector-${SpoltiDataSelectorComponent.nextId++}`;
  describedBy = '';
  isReady = false;

  filteredOptions: Observable<any[]> = of([]);
  private onChange: (value: number | null) => void = () => { };

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private fb: UntypedFormBuilder,
    private fm: FocusMonitor,
    private elRef: ElementRef<HTMLElement>
  ) {
    this.parts = this.fb.group({
      selectedId: ''
    });

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    this.fm.monitor(elRef.nativeElement, true).subscribe(origin => {
      this.focused = !!origin;
      this.stateChanges.next();
    });
  }

  ngOnInit(): void {



    this.initializeAutocompleteControls();
    this.isReady = true;
    this.updateGui();
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
  }

  writeValue(obj: number): void {
    this.value = obj;
  }

  registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void { }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    const control = this.parts.get('selectedId');
    if (isDisabled) control?.disable();
    else control?.enable();
  }

 get empty() {
  const val = this.control.value;
  return !val || (typeof val === 'string' && val.trim() === '');
}

  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this.elRef.nativeElement.querySelector('input')!.focus();
    }
  }

  updateGui(): void {
    if (this.isReady && this.getItemByIdFn && this.value) {
      this.getItemByIdFn(this.value).then(item => {
        // evita acionar valueChanges de forma desnecessária
        this.parts.get('selectedId')?.patchValue(item, { emitEvent: false });
      });
    }
  }


  initializeAutocompleteControls(): void {



    const control = this.parts.get('selectedId');
    if (!control) return;

    this.filteredOptions = this.control.valueChanges.pipe(
       startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((value: any) => {

        if (typeof value === 'string') {
          return this.searchFn(value);
        } else {

          this._value = value?.id ?? null;
          this.onChange(this._value);
          return of([]);
        }
      })
    );

    this.control.valueChanges.subscribe((value: any) => {
      if (typeof value !== 'string') {
        this._value = value?.id ?? null;
        this.onChange(this._value);
        this.idChanged.emit(this._value); // 🔔 emite para o pai
      }
    });

  }

onInputFocus(event: FocusEvent) {
  const input = event.target as HTMLInputElement;
  input.select();

  const current = this.control.value;
  if (typeof current === 'string') {
    this.control.markAsTouched();
    this.control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  // Abre o painel do autocomplete
  setTimeout(() => {
    this.autocompleteTrigger?.openPanel();
  }, 0);
}

}
